"use client";
import React, { useEffect, useRef, useState } from "react";
import "../app/project-price/project-price.css";
import { Col, Container, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Info } from "@/components/svg";
import CommonButton from "@/components/CommonButton";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useRouter, useSearchParams } from "next/navigation";
import {
  getLeadById,
  getQuestionOption,
  usersPerformance,
} from "@/services/pricing";
import { Loading } from "@/components/Loading";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import DetailsConformationModal from "@/components/DetailsConformationModal";
import PackageCard from "@/components/PackageCard";
import Link from "next/link";
import RedirectModal from "@/components/RedirectModal";
import PricingDetailsForm from "../components/PricingDetailsForm";
import { TestimonialData } from "../../utils/libs/utils";
import CustomPackageCard from "@/components/CustomPackageCard";
import DeveloperModal from "@/components/DeveloperModal";
import RateCardModal from "@/components/RateCardModal";
import { event } from "@/lib/analytics";
import GreetingModal from "@/components/greetingModal";
import { showToast } from "../../utils/libs/toast-utilis";
import { FaQuoteLeft, FaQuoteRight, FaStar } from "react-icons/fa";

// Tooltip data
const tooltipData = [
  {
    text: "End-to-End Software Development",
    tooltip: "From discovery to deployment...",
  },
  { text: "Modern Tech Stack", tooltip: "Built with the latest tools..." },
  {
    text: "Business-Centric Approach",
    tooltip: "Every feature is built with ROI in mind...",
  },
  {
    text: "Agile and Transparent Processes",
    tooltip: "Weekly sprint reviews...",
  },
  {
    text: "Custom & Industry-Specific Solutions",
    tooltip: "Specialized packages for industries...",
  },
  {
    text: "Global Clientele, Local Dedication",
    tooltip: "Serving clients across the globe...",
  },
  {
    text: "Trusted Delivery, Every Time",
    tooltip: "95%+ on-time delivery record...",
  },
  {
    text: "Security & Compliance Ready",
    tooltip: "GDPR, HIPAA ready solutions...",
  },
  {
    text: "AI-Enabled Development",
    tooltip: "AI-assisted design and dev tools...",
  },
  {
    text: "People-First Company",
    tooltip: "In-house developers, no outsourcing...",
  },
];

const TooltipListItem = ({ item }) => (
  <div className="d-flex gap-2 align-items-center mb-2">
    <li className="li-text">{item.text}</li>
    <OverlayTrigger
      placement="right"
      overlay={<Tooltip>{item.tooltip}</Tooltip>}
    >
      <span style={{ cursor: "pointer" }}>
        <Info />
      </span>
    </OverlayTrigger>
  </div>
);

const TestimonialCard = ({ text, name, location }) => (
  <div className="keen-slider__slide testimonial-card-main">
    <div className="testimonial-card">
      {/* <FaQuoteLeft className="quote-left" /> */}
      <p>
        <span>&ldquo;</span>
        {text}
      </p>

      <div>
        <div className="mb-2 d-flex align-items-center">
          <FaStar className="primary-color" />
          <FaStar className="primary-color" />
          <FaStar className="primary-color" />
          <FaStar className="primary-color" />
          <FaStar className="primary-color" />
        </div>
        <p className="p-0 m-0">
          <b>{name}</b>
        </p>
        <p className="p-0 m-0">{location}</p>
      </div>
      <FaQuoteRight className="quote-right" />
    </div>
  </div>
);

const ProjectPricePage = () => {
  const [leadDetails, setLeadDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeveloperModal, setDeveloperShowModal] = useState(false);
  const [showRateModal, setRateShowModal] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectedPackageName, setSelectedPackageName] = useState(null);
  // const [customerModalShow, setCustomerModalShow] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingTeamRole, setLoadingTeamRole] = useState(false);
  const [teamRoleDetails, setTeamRoleDetails] = useState(null);
  const [roleOption, setRoleOption] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const pdfRef = useRef();
  const searchParams = useSearchParams();
  const quoteId = searchParams.get("quoteId");
  const route = useRouter();
  useEffect(() => {
    setIsFlipped(true);
    const timer = setTimeout(() => setIsFlipped(false), 1000); // remove class after 1 second
    return () => clearTimeout(timer);
  }, []);
  const [sliderRef] = useKeenSlider({
    breakpoints: {
      "(min-width: 560px)": {
        slides: {
          perView: 1,
          spacing: 20,
        },
      },
      "(min-width: 768px)": {
        slides: {
          perView: 2,
          spacing: 25,
        },
      },
      "(min-width: 1024px)": {
        slides: {
          perView: 2,
          spacing: 25,
        },
      },
      "(min-width: 1200px)": {
        slides: {
          perView: 2.5,
          spacing: 30,
        },
      },
      "(min-width: 1201px)": {
        slides: {
          perView: 2.7,
          spacing: 30,
        },
      },
    },
  });

  useEffect(() => {
    if (quoteId) {
      fetchLeadDetails();
    } else {
      route?.push("/pricing");
    }
  }, [quoteId]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const data = await getLeadById(quoteId);
      const roleOptionRes = await getQuestionOption(
        "package/get-distinct-role"
      );
      setLeadDetails({
        ...data,
        package: [
          ...data?.package,
          {
            ...(data?.customPackage ? data?.customPackage : {}),
            isCustom: true,
            title: "Custom Package",
            industry_tags: [data?.lead.industries],
            complexity: data?.lead.complexity,
            duration_months: data?.lead?.duration,
            engagement_type: data?.lead.engagement?.[0],
            target_platforms: [data?.lead.platform],
            technical_tags: data?.lead?.platform?.technical_tags,
            isUpdate: !!data?.customPackage,
          },
        ],
      });
      setRoleOption(roleOptionRes);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleDetails = async () => {
    try {
      setLoadingTeamRole(true);
      const data = await getQuestionOption("team-role");
      event({
        action: "show team role",
        params: {
          page: "project_price_page",
          customerId: leadDetails?.lead?.CustomerId?._id,
          lead_id: quoteId,
        },
      });
      setTeamRoleDetails(data);
      setRateShowModal(true);
    } catch (error) {
    } finally {
      setLoadingTeamRole(false);
    }
  };
  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const response = await downloadLeadById(quoteId);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "estimation.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) return <Loading />;

  const userDetails = {
    name: "Savan Kheni",
    email: "savan@example.com",
    mobile: "+91 9876543210",
  };
  const bestFor = {
    0: "🔥 Best suited for {{test}}",
    1: "💸 Most Price effective",
    2: "🧑‍💻 Custom Teams",
  };
  return (
    <ProtectedRoute>
      <div className="about-hero">
        <Container>
          <Row>
            <Col md={12} className="align-self-center text-center">
              <h3 className="com-title animation-bottom">
                Hello, <span>{leadDetails?.lead?.CustomerId?.name}</span>
              </h3>
              <p className="animation-bottom text-center mt-4">
                Package Finance loan project has been carefully assembled based
                on your selected platform, complexity, and timeline — reflecting
                our proven project estimation logic. Our highly skilled team has
                delivered scalable and secure solutions across industries, and
                this package reflects the same precision and expertise. It’s
                fair, transparent, and built to help you move forward with
                confidence.
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        <Row className="mb-5 final-price gy-4">
          {leadDetails?.package?.length > 0 &&
            leadDetails?.package?.map((item, index) => {
              return (
                <Col
                  xl={4}
                  lg={4}
                  md={6}
                  sm={12}
                  className="d-flex"
                  key={index}
                >
                  <PackageCard
                    className={"animation-bottom"}
                    title={item?.title}
                    description={item?.description}
                    base_price={item?.base_price}
                    monthlyCost={item?.monthlyCost}
                    industry_tags={item?.industry_tags}
                    complexity={item?.complexity}
                    duration_months={item?.duration_months}
                    engagement_type={item?.engagement_type}
                    target_platforms={item?.target_platforms}
                    technical_tags={item?.technical_tags}
                    team_roles={item?.team_roles}
                    idealFor={item?.idealFor}
                    expire_date={item?.expire_date}
                    bestFor={bestFor?.[index]?.replaceAll(
                      "{{test}}",
                      leadDetails?.lead?.industries?.title
                    )}
                    onHandelSelect={async (isEditTeam = false) => {
                      if ((item?.isCustom && !item?.isUpdate) || isEditTeam) {
                        setDeveloperShowModal(true);
                        if (isEditTeam) {
                          event({
                            action: "edit custom team",
                            params: {
                              page: "project_price_page",
                              customerId: leadDetails?.lead?.CustomerId?._id,
                              lead_id: quoteId,
                              package: item,
                            },
                          });
                        } else {
                          event({
                            action: "select custom team",
                            params: {
                              page: "project_price_page",
                              lead_id: quoteId,
                              customerId: leadDetails?.lead?.CustomerId?._id,
                            },
                          });
                        }
                      } else {
                        setShowModal(true);
                        setSelectedPackageId(item?._id);
                        setSelectedPackageName(item?.title);
                        event({
                          action: "select package",
                          params: {
                            page: "project_price_page",
                            package: item,
                            lead_id: quoteId,
                            customerId: leadDetails?.lead?.CustomerId?._id,
                          },
                        });
                        try {
                          await usersPerformance({
                            payload: {
                              customStatus: "Interested",
                              packageId: [item?._id],
                            },
                            leadId: quoteId,
                          });
                        } catch (error) {
                          if (error?.message) {
                            showToast.error(error?.message);
                          }
                        }
                      }
                    }}
                    isCustomPackage={item?.isCustom}
                    isUpdate={item?.isUpdate}
                  />
                </Col>
              );
            })}
          {/* <Col xl={4} lg={4} md={6} sm={12} className="d-flex">
            <PackageCard
              className={isFlipped ? "flip-card" : ""}
              title={leadDetails?.package?.title}
              description={leadDetails?.package?.description}
              base_price={leadDetails?.package?.base_price}
              monthlyCost={leadDetails?.package?.monthlyCost}
              industry_tags={leadDetails?.package?.industry_tags}
              complexity={leadDetails?.package?.complexity}
              duration_months={leadDetails?.package?.duration_months}
              engagement_type={leadDetails?.package?.engagement_type}
              target_platforms={leadDetails?.package?.target_platforms}
              technical_tags={leadDetails?.package?.technical_tags}
              team_roles={leadDetails?.package?.team_roles}
              idealFor={leadDetails?.package?.idealFor}
              onHandelSelect={() => setShowModal(true)}
            />
          </Col>
          <Col xl={4} lg={4} md={6} sm={12} className="d-flex">
            <PackageCard
              className={isFlipped ? "flip-card" : ""}
              title={leadDetails?.package?.title}
              description={leadDetails?.package?.description}
              base_price={leadDetails?.package?.base_price}
              monthlyCost={leadDetails?.package?.monthlyCost}
              industry_tags={leadDetails?.package?.industry_tags}
              complexity={leadDetails?.package?.complexity}
              duration_months={leadDetails?.package?.duration_months}
              engagement_type={leadDetails?.package?.engagement_type}
              target_platforms={leadDetails?.package?.target_platforms}
              technical_tags={leadDetails?.package?.technical_tags}
              team_roles={leadDetails?.package?.team_roles}
              idealFor={leadDetails?.package?.idealFor}
              onHandelSelect={() => setShowModal(true)}
            />
          </Col> */}
          {/* <Col xl={4} lg={4} md={6} sm={12} className="d-flex">
            <CustomPackageCard
              className={isFlipped ? "flip-card" : ""}
              title={leadDetails?.package?.title}
              description={leadDetails?.package?.description}
              base_price=""
              packagePrice="Price depend on team"
              industry_tags={leadDetails?.package?.industry_tags}
              effectiveRate="Depends on team"
              complexity={leadDetails?.package?.complexity}
              duration_months={leadDetails?.package?.duration_months}
              engagement_type={leadDetails?.package?.engagement_type}
              target_platforms={leadDetails?.package?.target_platforms}
              technical_tags={leadDetails?.package?.technical_tags}
              team_roles={leadDetails?.package?.team_roles}
              idealFor={leadDetails?.package?.idealFor}
              monthlyCost={leadDetails?.package?.monthlyCost}
              onHandelModal={() => setDeveloperShowModal(true)}
            />
          </Col> */}
          <Col xl={12}>
            <div className="mt-3 disclaimer">
              <span>
                <strong>Disclaimer</strong>
              </span>
              <p>
                The package details, pricing, and timelines provided above are
                based on the preliminary understanding of your project
                requirements. Final estimates may vary after in-depth
                discussions and implication of additional team roles.
              </p>
            </div>
          </Col>
          <div className="text-section mb-3 p-3 rounded bg-light">
            <Row className="gy-3">
              <Col md={9} className="align-self-center">
                <p className="mb-0 text-center text-md-start">
                  We have vast domains of{" "}
                  <span className="primary-color">experts!</span> Wanted to
                  check and <span className="primary-color">review</span> their{" "}
                  <span className="primary-color">rates?</span>
                </p>
              </Col>
              <Col md={3}>
                <CommonButton
                  label="Check Now!"
                  className="black-border d-block mx-auto ms-md-auto mx-md-0"
                  variant="outline"
                  disabled={loadingTeamRole}
                  onClick={() => {
                    fetchRoleDetails();
                  }}
                />
              </Col>
            </Row>
          </div>
          <Col xl={12} lg={12} className="responsive-order-2">
            <div className="com-title mb-4">
              Why <span>us?</span>
            </div>
            <Row>
              <Col lg={6}>
                {tooltipData.slice(0, 5).map((item, i) => (
                  <TooltipListItem key={i} item={item} />
                ))}
              </Col>
              <Col lg={6}>
                {tooltipData.slice(5).map((item, i) => (
                  <TooltipListItem key={i + 5} item={item} />
                ))}
              </Col>
            </Row>

            <div className="com-title mt-5">
              Trusted by <span>our clients</span>
            </div>
            <div
              ref={sliderRef}
              className="keen-slider testimonial-keen-slider testimonial-keen-slider-responsive"
            >
              {TestimonialData.map((item, i) => (
                <TestimonialCard key={i} {...item} />
              ))}
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start gap-3 mt-5 custom-fit-wrap">
              <div className="com-title m-0 text-center text-lg-start">
                Didn’t find the{" "}
                <span className="highlight-orange">perfect fit?</span> Relax,
                We’ve got more packages for you.
              </div>
              <CommonButton
                label="Explore More"
                onClick={() => {
                  event({
                    action: "Explore More packages",
                    params: {
                      page: "project_price_page",
                      leadId: quoteId,
                    },
                  });
                  setShowRedirectModal(true);
                }}
                className="explore-btn"
              />
            </div>
          </Col>
        </Row>
      </Container>
      <DetailsConformationModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        details={{
          name: leadDetails?.lead?.CustomerId?.name,
          mobile: leadDetails?.lead?.CustomerId?.mobile_number,
          email: leadDetails?.lead?.CustomerId?.email,
          _id: leadDetails?.lead?.CustomerId?._id,
        }}
        onUpdate={() => {
          fetchLeadDetails();
        }}
        quoteId={quoteId}
        selectedPackageId={selectedPackageId}
        selectedPackageName={selectedPackageName}
        // secondBtn={() => setCustomerModalShow(true)}
      />
      {/* <PricingDetailsForm
        modalShow={customerModalShow}
        setModalShow={setCustomerModalShow}
        userRecord={{
          name: leadDetails?.lead?.CustomerId?.name,
          mobile: leadDetails?.lead?.CustomerId?.mobile_number,
          email: leadDetails?.lead?.CustomerId?.email,
          _id: leadDetails?.lead?.CustomerId?._id,
        }}
        isEdit={true}
        onUpdate={() => {
          setCustomerModalShow(false);
          fetchLeadDetails();
        }}
      /> */}
      <RedirectModal
        show={showRedirectModal}
        onHide={() => {
          setShowRedirectModal(false);
        }}
        quoteId={quoteId}
      />
      <DeveloperModal
        show={showDeveloperModal}
        checkRate={() => {
          fetchRoleDetails();
        }}
        onHide={() => {
          setDeveloperShowModal(false);
        }}
        loadingTeamRole={loadingTeamRole}
        roleOption={roleOption}
        customTeamRole={leadDetails?.customPackage}
        fetchLeadDetails={fetchLeadDetails}
        quoteId={quoteId}
      />
      <RateCardModal
        show={showRateModal}
        onHide={() => {
          setRateShowModal(false);
        }}
        teamRoleDetails={teamRoleDetails}
      />
    </ProtectedRoute>
  );
};

export default ProjectPricePage;
